import { StudentAssignmentCubit } from '@breakoutlearning/firebase-repository/cubits/StudentAssignmentCubit'
import {
  AssignmentState,
  LibraryObjectState,
} from '@breakoutlearning/firebase-repository/types'
import { PageTitle } from 'components/PageTitle'
import { CircleAlert } from 'components/icons/CircleAlert'
import { BreakoutAlert } from 'components/design-system/BreakoutAlert'
import { BreakoutButton } from 'components/design-system/BreakoutButton'
import { formatDateTime } from 'util/dates'
import { XSmallIcon } from 'components/icons/XSmall'
import { useRepository } from 'hooks/auth'
import { useCubitBuilder } from 'hooks/cubits'
import {
  StudentAssignmentCubitProvider,
  useStudentAssignmentCubit,
} from 'hooks/cubits/studentAssignment'
import { useDialogs } from 'hooks/dialogs'
import { useBreakoutUser } from 'hooks/profile'
import { useRootStore } from 'hooks/rootStore'
import { useLTIMode } from 'hooks/use-lti-mode'
import { useTranslationTyped } from 'i18n/i18n'
import { reaction } from 'mobx'
import { observer } from 'mobx-react-lite'
import { useEffect, useMemo } from 'react'
import { toast } from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { StudentAssignmentEnroll } from './StudentAssignmentEnrollment'
import { StudentAssignmentHeader } from './StudentAssignmentHeader'
import {
  StudentAssignmentInfo,
  StudentAssignmentInfoLoading,
} from './StudentAssignmentInfo'
import { StudentAssignmentLibraryActionParser } from './StudentAssignmentLibraryActionParser'
import {
  StudentAssignmentLoadingPage,
  StudentAssignmentPendingPage,
} from './StudentAssignmentPendingPage'
import {
  StudentAssignmentSteps,
  StudentAssignmentStepsLoading,
} from './StudentAssignmentSteps'
import { StudentAssignmentJoinSessionDialog } from './dialogs/JoinSessionDialog'

export const StudentAssignmentPage = observer(function StudentAssignmentPage({
  assignmentId,
  sectionId,
  roomStateId,
}: {
  assignmentId: string
  sectionId: string
  roomStateId: string | undefined
}) {
  const { showDialog, dialogStore } = useDialogs()
  const store = useRootStore()
  const repository = useRepository()
  const { ltiMode } = useLTIMode()
  const { tt } = useTranslationTyped()

  const cubit = useCubitBuilder(
    () =>
      new StudentAssignmentCubit(repository, {
        sectionId,
        assignmentId,
        roomStateId,
        onRoomLeft: () => {
          // The only path to the no-room `assignment` route: an explicit,
          // user-initiated leave. Never an automatic reaction. Replace, so
          // Back skips the room the student just left.
          store.navigateTo(
            'assignment',
            { sectionId, assignmentId },
            undefined,
            { replaceHistory: true }
          )
        },
        onSectionFetchPermissionError: () => {
          store.navigateTo('home')
          const className = cubit.section.data.className
          let body =
            tt.student_assignment.toast_unnamed_section_fetch_permission_error()
          if (className) {
            body = tt.student_assignment.toast_section_fetch_permission_error({
              className,
            })
          }
          toast.error(body, { duration: 8 * 1000 })
        },
      }),
    [repository]
  )

  // Keep the cubit's room selection in sync with the URL without remounting.
  // roomStateId is no longer part of the page's remount key (see
  // StudentLibrary), so on a room change TwoColumnAnimatedPane keeps the same
  // key and does NOT recompute the controller — which means the `roomStateId`
  // *prop* can go stale. So read the live room from the router (this observer
  // re-renders when router.params changes) rather than the prop. This pushes
  // URL -> cubit; the reaction below pushes cubit -> URL. The equality check is
  // the explicit stop condition: when the two already agree we do nothing, so
  // the two syncs settle at a fixed point instead of ping-ponging.
  const routeRoomStateId =
    store.router.params?.roomStateId?.toString() || undefined
  useEffect(() => {
    if (cubit.roomStateId !== routeRoomStateId) {
      cubit.changeRoomStateId(routeRoomStateId)
    }
  }, [routeRoomStateId, cubit])

  useEffect(() => {
    // Sync the URL INTO a room when the cubit selects one, and only ever that
    // direction. We deliberately never auto-navigate back to the plain
    // `assignment` route: that clear->redirect->re-push cycle was the
    // getSlideDeckForAssignment remount ring. Leaving a room / dropping to the
    // no-room view is always an explicit user action (see the leave handlers and
    // the no-access pane), never an automatic reaction.
    return reaction(
      // isSolo reads a default until the assignment doc arrives, so the
      // room-id change must wait for it before deciding whether to navigate
      () => ({
        roomStateId: cubit.roomStateId,
        assignmentLoaded: cubit.assignment.isLoaded,
      }),
      ({ roomStateId, assignmentLoaded }) => {
        if (!assignmentLoaded) return
        // no room selected: never redirect to `assignment` — that is impossible
        // by design here.
        if (!roomStateId) return
        // solo rooms are an implementation detail: keep the plain assignment
        // URL, because switching routes remounts the page and disposes the
        // cubit that an open quiz dialog is still bound to
        if (cubit.assignment.isSolo) return
        // already in sync: navigating to the same URL would only steal focus
        // (navigateTo resets it to the top anchor)
        const routerRoomStateId =
          store.router.params?.roomStateId?.toString() || undefined
        if (roomStateId === routerRoomStateId) return
        // replace: the room is a sub-selection of this assignment, so Back
        // should leave the assignment, not show the pre-join view
        store.navigateTo(
          'assignmentWithRoom',
          {
            assignmentId: cubit.assignmentId,
            sectionId: cubit.sectionId,
            roomStateId: roomStateId,
          },
          undefined,
          { replaceHistory: true }
        )
      }
    )
  }, [cubit, store])

  useEffect(() => {
    const disposeJoinDialogReaction = reaction(
      () => ({
        canPop: cubit.canPopJoinDialog && dialogStore.dialogs.length === 0,
      }),
      ({ canPop }, prev) => {
        if (!canPop || prev?.canPop === canPop) return
        showDialog(
          ({ remove }) => (
            <StudentAssignmentJoinSessionDialog
              cubit={cubit}
              closeSelf={remove}
            />
          ),
          {
            namespace: 'join-session',
            ignoreOnNamespace: true,
          }
        )
      },
      { fireImmediately: true }
    )
    return () => {
      disposeJoinDialogReaction()
    }
  }, [dialogStore, cubit, showDialog])

  if (ltiMode) {
    if (!cubit.isReady) return <StudentAssignmentLoadingPage />
    if (
      cubit.assignment.data.assignmentState === AssignmentState.unconfigured
    ) {
      return <StudentAssignmentPendingPage />
    }
  }

  return (
    <StudentAssignmentCubitProvider value={cubit}>
      <PageTitle title={cubit.assignment.slideDeck.data.slideDeckName} />
      <div
        className="z-50 h-full p-5 md:p-10"
        data-testid="student-assignment-page"
      >
        <div className="items-left flex h-full flex-col align-middle">
          <StudentAssignmentHeader />
          <NotYetAvailableNotice />
          <div className="mt-2 grid grow rounded-3xl bg-surface p-5 md:overflow-auto lg:overflow-hidden">
            <div className="grid h-full max-h-full grid-cols-1 grid-rows-2 flex-col-reverse items-center lg:grid-cols-2 lg:grid-rows-1 lg:overflow-hidden">
              <StudentAssignmentView />
            </div>
          </div>
        </div>
      </div>
      <StudentAssignmentLibraryActionParser />
    </StudentAssignmentCubitProvider>
  )
})

const NotYetAvailableNotice = observer(function NotYetAvailableNotice() {
  const cubit = useStudentAssignmentCubit()
  const { tt } = useTranslationTyped()
  const inFuture = cubit.assignedAtIsInFutureForCurrentUser
  // The gate above honors the student's individual availableFrom, so the
  // displayed date must too — the base assignedAtDate can already be in the
  // past for a student whose override opens later.
  const assignedAtDate = cubit.effectiveAssignedAtDate

  // isSolo reads a default until the assignment doc arrives.
  if (!inFuture || !assignedAtDate || !cubit.assignment.isLoaded) return null

  return (
    <div data-testid="assignment-not-yet-available-notice" className="mt-2">
      <BreakoutAlert variant="warning">
        {cubit.assignment.isSolo
          ? tt.student_assignment.solo_not_yet_available_notice({
              date: formatDateTime(assignedAtDate),
            })
          : tt.student_assignment.not_yet_available_notice({
              date: formatDateTime(assignedAtDate),
            })}
      </BreakoutAlert>
    </div>
  )
})

const StudentAssignmentView = observer(function StudentAssignmentView() {
  const {
    isReady,
    roomAccessDenied,
    libraryObject: { requiresPayment, libraryObjectState },
  } = useStudentAssignmentCubit()

  const isExpired = libraryObjectState === LibraryObjectState.expired

  const isCanceled = libraryObjectState === LibraryObjectState.canceled

  const AssignmentInfoView = isReady ? (
    <StudentAssignmentInfo />
  ) : (
    <StudentAssignmentInfoLoading />
  )

  const AssignmentMainView = useMemo(() => {
    // Check access before readiness: the room selection resolves early, and we
    // want the no-access pane to show promptly instead of a spinner.
    if (roomAccessDenied) return <NoRoomAccessView />
    if (!isReady) return <StudentAssignmentStepsLoading />
    if (isCanceled) return <CanceledView />
    if (requiresPayment) return <StudentAssignmentEnroll />
    if (isExpired) return <ExpiredView />
    return <StudentAssignmentSteps />
  }, [isReady, requiresPayment, isExpired, isCanceled, roomAccessDenied])

  return (
    <>
      {AssignmentInfoView}
      {AssignmentMainView}
    </>
  )
})

const ExpiredView = observer(function ExpiredView() {
  const { t } = useTranslation()

  return (
    <div
      data-testid="assignment-expired-view"
      className="flex h-full w-full flex-col items-center justify-center gap-3 rounded-3xl bg-core-tertiary px-9 py-5"
    >
      <CircleAlert size={20} className="stroke-breakout-red" />
      <h2
        data-testid="assignment-expired-heading"
        className="text-title-medium"
      >
        {t('student_assignment.assignment_expired')}
      </h2>
      <p
        data-testid="assignment-expired-description"
        className="max-w-[350px] text-body-medium"
      >
        {t('student_assignment.assignment_expired_description')}
      </p>
    </div>
  )
})

export const NoRoomAccessView = observer(function NoRoomAccessView() {
  const { t } = useTranslation()
  const store = useRootStore()
  const cubit = useStudentAssignmentCubit()

  // ENG-132: a student reassigned to another group while sitting here must not
  // be redirected; the button turns into a link to the new group instead.
  // Solo rooms are not groups: the plain assignment route resumes them.
  const groupRoomId = cubit.assignment.isSolo
    ? undefined
    : cubit.getCurrentUserRoomId()

  return (
    <div
      data-testid="assignment-no-room-access-view"
      className="flex h-full w-full flex-col items-center justify-center gap-3 rounded-3xl bg-core-tertiary px-9 py-5 text-center"
    >
      <CircleAlert size={20} className="stroke-breakout-red" />
      <h2
        data-testid="assignment-no-room-access-heading"
        className="text-title-medium"
      >
        {t('student_assignment.no_room_access_title')}
      </h2>
      {/* status: announces the switch to the has-group copy when the
          instructor reassigns the student while they wait here */}
      <p role="status" className="max-w-[350px] text-body-medium">
        {groupRoomId
          ? t('student_assignment.no_room_access_description_has_group')
          : t('student_assignment.no_room_access_description')}
      </p>
      <BreakoutButton
        data-testid="assignment-no-room-access-button"
        onClick={() => {
          // replace both ways: Back should not return to this dead-end room
          if (groupRoomId) {
            store.navigateTo(
              'assignmentWithRoom',
              {
                sectionId: cubit.sectionId,
                assignmentId: cubit.assignmentId,
                roomStateId: groupRoomId,
              },
              undefined,
              { replaceHistory: true }
            )
            return
          }
          // Solo rooms never put their id in the URL, so a solo student is
          // already on the `assignment` route: navigating there changes
          // nothing and the URL->cubit sync never clears the room. Clear it
          // here so the pane goes away in both cases.
          cubit.changeRoomStateId(undefined)
          store.navigateTo(
            'assignment',
            {
              sectionId: cubit.sectionId,
              assignmentId: cubit.assignmentId,
            },
            undefined,
            { replaceHistory: true }
          )
        }}
      >
        {groupRoomId
          ? t('student_assignment.no_room_access_go_to_group_button')
          : t('student_assignment.no_room_access_button')}
      </BreakoutButton>
    </div>
  )
})

const CanceledView = observer(function CanceledView() {
  const { t } = useTranslation()
  const user = useBreakoutUser()
  const cubit = useStudentAssignmentCubit()

  const tokensAvailable = user.availableTokens

  // todo(ashold12): modified copy for section pass assignments
  return (
    <div className="flex h-full w-full flex-col items-center justify-center gap-3 rounded-3xl bg-core-tertiary px-9 py-5 text-center">
      <XSmallIcon size={30} className="stroke-core-error" />
      <div className="flex max-w-[366px] flex-col gap-1">
        <h2 className="text-title-large">
          {t('student_assignment.assignment_canceled')}
        </h2>
        {!cubit.section.usesSectionPass && (
          <p className="max-w-[350px] text-body-medium">
            {t('student_assignment.assignment_canceled_description')}
          </p>
        )}
      </div>
      {!cubit.section.usesSectionPass && (
        <div className="flex items-center">
          <p className="text-body-medium">
            {t('student_assignment.tokens_available_title') + ' ='}&nbsp;
          </p>
          <strong className="text-title-small text-core-error">
            {t('student_library.token_number', { count: tokensAvailable })}
          </strong>
        </div>
      )}
    </div>
  )
})
